import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { photoUrlFromKey } from '@leanin/shared/media-url';
import { useMember } from '@/lib/use-session';
import { SpaceDetailView } from '@/components/spaces/SpaceDetailView';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import {
  fetchSpace,
  fetchRelatedSpaces,
  spacesKeys,
} from '@/lib/queries/spaces';
import { fetchGroupMembers, groupKeys } from '@/lib/queries/groups';
import { fetchFeed, feedKeys } from '@/lib/queries/feed';

export const Route = createFileRoute('/_authed/groups/$id/')({
  component: SpaceDetailPage,
});

// The route owns every query the detail page needs and hands each slice to
// `SpaceDetailView` as `T | null` (or `undefined` while pending). The view
// renders its skeleton chrome + per-region fallbacks until each slice
// resolves — there is no parallel skeleton tree.
function SpaceDetailPage() {
  const { id } = Route.useParams();
  const me = useMember();

  const spaceQuery = useQuery({
    queryKey: spacesKeys.detail(id),
    queryFn: () => fetchSpace(id),
    retry: false,
  });

  const membersQuery = useQuery({
    queryKey: groupKeys.members(id, { pageSize: 50 }),
    queryFn: () => fetchGroupMembers({ groupId: id, pageSize: 50 }),
    enabled: spaceQuery.data != null,
  });
  const relatedQuery = useQuery({
    queryKey: spacesKeys.related(id),
    queryFn: () => fetchRelatedSpaces(id),
    enabled: spaceQuery.data != null,
  });
  const postsQuery = useQuery({
    queryKey: feedKeys.list({ scope: `group:${id}`, pageSize: 24 }),
    queryFn: () => fetchFeed({ scope: `group:${id}`, pageSize: 24 }),
    enabled: spaceQuery.data != null,
  });

  if (me === null) return null;
  // 404 → branded NotFoundPage directly (render-context `throw notFound()`
  // lands in TanStack's catch-boundary, not its 404 surface).
  if (
    isNotFoundApiError(spaceQuery.error) ||
    (spaceQuery.data === null && !spaceQuery.isLoading)
  ) {
    return <NotFoundPage />;
  }
  if (spaceQuery.isError) throw notFound();

  const space = spaceQuery.data ?? null;
  const members = membersQuery.data?.items ?? null;
  const related = relatedQuery.data ?? null;
  const posts = postsQuery.data?.items ?? null;

  const viewer = {
    id: me.id,
    firstName: me.firstName,
    lastName: me.lastName,
    photoUrl: photoUrlFromKey(me.photoStorageKey, me.photoStatus) ?? undefined,
  };

  return (
    <SpaceDetailView
      space={space}
      viewer={viewer}
      members={members}
      related={related}
      posts={posts}
    />
  );
}
